// The account menu in the app bar (see `features/user-settings.md`): one avatar
// button that collapses everything about "me" — who I am signed in as, my
// settings, my athlete profile, and signing out — into a single control.
//
// It replaces the loose row of trailing buttons each surface used to carry, and
// is shared by all three of them (desktop admin `Layout`, `MobileAdminView`,
// athlete `AthleteHomeView`) so the account lives in the same place everywhere.
// The language picker deliberately stays *outside* the menu: burying it behind
// an avatar would hide the one control a user who cannot read the current
// language needs first.

import { useState, type MouseEvent } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { AccountSettingsDialog } from './AccountSettingsDialog';
import { PasskeysDialog } from './PasskeysDialog';
import { accountInitials } from './labels';

interface AccountMenuProps {
  /**
   * Open the athlete surface. Present only for an account that also holds an
   * athlete profile of its own (see `features/athlete-access.md`).
   */
  onMyProfile?: () => void;
  /**
   * Leave a reduced surface for the full desktop site — the mobile admin view
   * and the athlete home each offer this. `fullSiteLabel` is that surface's own
   * wording, since the two differ.
   */
  onFullSite?: () => void;
  fullSiteLabel?: string;
}

export function AccountMenu({
  onMyProfile,
  onFullSite,
  fullSiteLabel,
}: AccountMenuProps) {
  const { me, logout } = useAuth();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [passkeysOpen, setPasskeysOpen] = useState(false);
  const open = Boolean(anchorEl);

  const close = () => setAnchorEl(null);
  // Every item closes the menu first, so the surface it opens (a dialog, the
  // athlete view, the login screen) is never left behind an open popover.
  const run = (action: () => void) => () => {
    close();
    action();
  };

  const email = me?.user.email ?? '';
  const name = me?.user.name ?? '';

  return (
    <>
      <Tooltip title={t('account.menu.label')}>
        <IconButton
          onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
          aria-label={t('account.menu.label')}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{ flexShrink: 0, p: 0.5 }}
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              fontSize: 14,
              fontWeight: 700,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            {accountInitials(name, email)}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 240, borderRadius: 2 } } }}
      >
        {/* Identity header — which account this is, which matters most on the
            shared laptops a club runs its tournaments from. */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {name.trim().length > 0 ? name : t('account.menu.noName')}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {email}
          </Typography>
        </Box>
        <Divider />

        {onMyProfile && (
          <MenuItem onClick={run(onMyProfile)}>
            <ListItemIcon>
              <DirectionsRunIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('admin.common.myProfile')}</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={run(() => setSettingsOpen(true))}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('account.settings.title')}</ListItemText>
        </MenuItem>

        {/* Passkey management (see `features/passkey-login.md`) — in the
            account menu so every surface reaches it in the same place. */}
        <MenuItem onClick={run(() => setPasskeysOpen(true))}>
          <ListItemIcon>
            <KeyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('passkeys.manage')}</ListItemText>
        </MenuItem>

        {onFullSite && (
          <MenuItem onClick={run(onFullSite)}>
            <ListItemIcon>
              <DesktopWindowsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{fullSiteLabel ?? t('account.menu.fullSite')}</ListItemText>
          </MenuItem>
        )}

        <Divider />
        <MenuItem onClick={run(logout)}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('account.menu.signOut')}</ListItemText>
        </MenuItem>
      </Menu>

      <AccountSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      <PasskeysDialog open={passkeysOpen} onClose={() => setPasskeysOpen(false)} />
    </>
  );
}
